import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Clock, Music2, Award } from 'lucide-react';
import styles from './home.module.css';
import backgroundImage from '../assets/backgroundImage.png';
import pianoBackground from '../assets/pianoBackground.png';
import aboutImage from '../assets/aboutImage.png';
import drumsImage from '../assets/drums.png';
import pianoImage from '../assets/piano.png';
import guitarImage from '../assets/guitar.png';
import violinImage from '../assets/violin.png';
import keyboardImage from '../assets/keyboard.png';

// WhatsApp Configuration - Change this number to your business WhatsApp number
const WHATSAPP_NUMBER = '919080465878'; 



// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
 
};

// Custom hook for scroll animations
const useScrollAnimation = () => {
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      `.${styles['fade-in-up']}, .${styles['fade-in-left']}, .${styles['fade-in-right']}, .${styles['scale-in']}`
    );

    animatedElements.forEach(el => observer.observe(el));

    return () => {
      animatedElements.forEach(el => observer.unobserve(el));
    };
  }, []);
};

// Custom hook for counting animation
const useCountAnimation = (end, duration = 2000, suffix = '') => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const countRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.5,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          const startTime = Date.now();
          const endValue = parseFloat(end);
          
          const animate = () => {
            const now = Date.now();
            const progress = Math.min((now - startTime) / duration, 1);
            const easeOutQuad = progress * (2 - progress);
            const currentCount = easeOutQuad * endValue;
            
            setCount(currentCount);
            
            if (progress < 1) {
              requestAnimationFrame(animate);
            } else {
              setCount(endValue);
            }
          };
          
          animate();
        }
      });
    }, observerOptions);

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      if (countRef.current) {
        observer.unobserve(countRef.current);
      }
    };
  }, [end, duration, hasAnimated]);

  const displayValue = suffix === '+' 
    ? `${Math.floor(count)}${suffix}`
    : count.toFixed(1);

  return [displayValue, countRef];
};

const Home = () => {
  useScrollAnimation();

  const handleEnrollClick = () => {
    const message = encodeURIComponent(
      "Hi! I'm interested in enrolling for music classes at Vibe Musicals. Could you please provide me with more information?"
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div>
      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section 
        id="home"
        className={styles.hero}
        style={{ 
          backgroundImage: `url(${backgroundImage})`,
          backgroundPosition: "50% -2%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}
      >
        <div className={styles.heroOverlay}></div>
        
        <div className={styles.heroContent}>
          <h1 className={`${styles.title} ${styles['fade-in-up']}`}>
            One Stop for All<br />
            <span className={styles.musicText}>Music</span> Needs
          </h1>
          
          <p className={`${styles.subtitle} ${styles['fade-in-up']}`}>
            From beginner lessons to professional showcases, Vibe Musicals <br/>
            offers a complete journey through sound—tailored courses,<br/>
            expert guidance, and a portfolio that grows with your passion.
          </p>
          
          <button 
            className={`${styles.enrollButton} ${styles['fade-in-up']}`}
            onClick={handleEnrollClick}
          >
            Enroll
          </button>
        </div>
        
        <div className={styles.chevron}>
          <ChevronDown size={48} color="white" strokeWidth={2} />
        </div>
      </section>

      {/* Stats Section */}
      <section className={styles.statsSection}>
        <div className={styles.statsContainer}>
          <StatCard value={10} suffix="+" label="Years Experience" icon={Clock} />
          <StatCard value={500} suffix="+" label="Students Trained" icon={Music2} />
          <StatCard value={4.9} suffix="/5" label="Top Rated Training" icon={Award} />
        </div>
      </section>

      {/* About Section */}
      <About />
      
      {/* Instruments Section */}
      <Instruments />
      
    </div>
  );
};

// StatCard component
const StatCard = ({ value, suffix, label, icon: Icon }) => {
  const [displayValue, countRef] = useCountAnimation(value, 2000, suffix);

  return (
    <div ref={countRef} className={`${styles.statCard} ${styles['scale-in']}`}>
      <div className={styles.iconWrapper}>
        <Icon size={56} color="#FFA500" strokeWidth={2} />
      </div>
      <h2 className={styles.statNumber}>{displayValue}</h2>
      <p className={styles.statLabel}>{label}</p>
    </div>
  );
};

const About = () => {
  return (
    <section 
      id="about"
      className={styles.aboutSection}
      style={{ 
        backgroundImage: `url(${pianoBackground})` 
      }}
    >
      <div className={styles.aboutOverlay}></div>
      
      <div className={styles.aboutContainer}>
        <h2 className={`${styles.aboutTitle} ${styles['fade-in-up']}`}>About Us</h2>
        
        <div className={styles.aboutContent}>
          <div className={`${styles.aboutImageWrapper} ${styles['fade-in-left']}`}>
            <img 
              src={aboutImage} 
              alt="Music lesson"
              className={styles.aboutImage}
            />
          </div>
          
          <div className={`${styles.aboutText} ${styles['fade-in-right']}`}>
            <p className={styles.aboutParagraph}>
              Welcome to your one-stop destination for all things music. Whether you're a seasoned 
              musician or just starting your journey, our platform brings together a curated collection of 
              instruments, expert resources, and a vibrant community to fuel your passion. With over 100+ 
              learners and their stellar 4.9/5 user rating, we're committed to making music accessible, 
              inspiring, and deeply personal. From piano to violin and beyond, we help you find your sound 
              and make it sing.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Instruments = () => {
  const instruments = [
    { name: 'DRUMS', image: drumsImage },
    { name: 'PIANO', image: pianoImage },
    { name: 'GUITAR', image: guitarImage },
    { name: 'VIOLIN', image: violinImage },
    { name: 'KEYBOARD', image: keyboardImage }
  ];

  return (
    <section id="instruments" className={styles.instrumentsSection}>
      <div className={styles.instrumentsContainer}>
        <h2 className={`${styles.instrumentsTitle} ${styles['fade-in-up']}`}>Instrument</h2>
        
        <div className={styles.instrumentsGrid}>
          {instruments.slice(0, 3).map((instrument, index) => (
            <div key={index} className={`${styles.instrumentCard} ${styles['scale-in']}`}>
              <div className={styles.instrumentIconWrapper}>
                <img 
                  src={instrument.image} 
                  alt={instrument.name}
                  className={styles.instrumentIcon}
                />
              </div>
              <h3 className={styles.instrumentName}>{instrument.name}</h3>
            </div>
          ))}
        </div>
        
        <div className={styles.instrumentsGridBottom}>
          {instruments.slice(3, 5).map((instrument, index) => (
            <div key={index} className={`${styles.instrumentCard} ${styles['scale-in']}`}>
              <div className={styles.instrumentIconWrapper}>
                <img 
                  src={instrument.image} 
                  alt={instrument.name}
                  className={styles.instrumentIcon}
                />
              </div>
              <h3 className={styles.instrumentName}>{instrument.name}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};







export default Home;
